import React, { useEffect, useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import PatternLock from "react-pattern-lock";
import UAParser from "ua-parser-js";
import { useAdminLoggedInMutation } from "../redux/features/admin/adminApi";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { jwtDecode } from "jwt-decode";

const schema = Yup.object().shape({
  email: Yup.string()
    .email("Invalid email!")
    .required("Please enter your email!"),
  password: Yup.string().required("Please enter your password!").min(6),
});

const Login = () => {
  const [adminLoggedIn, { isSuccess, isError, error, data }] =
    useAdminLoggedInMutation();
  const [path, setPath] = useState([]);
  const [disabled, setDisabled] = useState(false);
  const [newPath, setNewPath] = useState("");
  const [isPatternValid, setIsPatternValid] = useState(false);
  const [mounted, setMounted] = useState(false);

  const navigate = useNavigate();

  const fetchIP = async () => {
    try {
      const res = await fetch("https://api.ipify.org");
      const data = await res.text();
      return data;
    } catch (error) {
      console.error("Error fetching IP:", error);
      return "unknown";
    }
  };

  function getpath() {
    if (!path || path.length === 0) return;
    
    let newPathString = "";
    for (let x of path) {
      const num = parseInt(x);
      if (!isNaN(num)) {
        newPathString += (num + 1).toString();
      }
    }
    
    if (newPathString.length > 0) {
      setNewPath(newPathString);
      if (newPathString.length < 3) {
        toast.error("Pattern must contain at least 3 dots");
        setIsPatternValid(false);
        setNewPath("");
        setPath([]);
      } else {
        setIsPatternValid(true);
      }
    }
  }

  const getDeviceInfo = () => {
    const parser = new UAParser();
    const result = parser.getResult();

    return {
      userAgent: navigator.userAgent,
      os: result.os.name,
      browser: result.browser.name,
      device: result.device.model || "unknown",
      os_version: result.os.version,
      browser_version: result.browser.version,
    };
  };

  const reset = () => {
    setDisabled(false);
    setPath([]);
    setIsPatternValid(false);
  };

  // Redirect if already logged in (run only on mount)
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (token && window.location.pathname === "/login") {
      try {
        const decodedToken = jwtDecode(token);
        const currentTime = Date.now() / 1000;
        if (decodedToken.exp > currentTime) {
          navigate("/");
        }
      } catch (e) {
        // Invalid token, do nothing (stay on login)
      }
    }
    // eslint-disable-next-line
  }, []);

  // Handle login result
  useEffect(() => {
    if (isSuccess && data?.status === true) {
      localStorage.setItem("token", data.token);
      localStorage.setItem("permission", JSON.stringify(data.permission));
      localStorage.setItem("role", data.role);
      toast.success(data.message);
      navigate("/");
    } else if (isSuccess && data?.status !== true) {
      toast.error(data.message);
    }
  }, [isSuccess, data, navigate]);

  useEffect(() => { setMounted(true); }, []);

  const formik = useFormik({
    initialValues: { email: "", password: "" },
    validationSchema: schema,
    onSubmit: async (data) => {
      if (!isPatternValid) {
        toast.error("Please create a valid pattern first");
        return;
      }

      try {
        const mydata = {
          deviceInfo: getDeviceInfo(),
          ipaddress: {
            ip: await fetchIP(),
          },
          username: data.email,
          password: data.password,
          pattern: newPath,
        };

        await adminLoggedIn(mydata);
        reset();
      } catch (err) {
        console.error("Login error:", err);
        toast.error("An error occurred during login");
      }
    },
  });

  const { values, errors, touched, handleChange, handleSubmit } = formik;

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
      className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-100"
    >
      <div className="max-w-md w-full mx-auto bg-white rounded-2xl shadow-xl overflow-hidden">
        <motion.div 
          initial={{ y: -20 }}
          animate={{ y: 0 }}
          className="bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-center"
        >
          <h2 className="text-2xl font-bold text-white">Admin Login</h2>
        </motion.div>

        <form
          onSubmit={handleSubmit}
          className="p-8 space-y-6"
        >
          <div className="space-y-4">
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                type="email"
                name="email"
                id="email"
                onChange={handleChange}
                value={values.email}
                placeholder="Enter your email"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
              />
              {errors.email && touched.email && (
                <motion.span 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-500 text-sm mt-1 block"
                >
                  {errors.email}
                </motion.span>
              )}
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <input
                type="password"
                name="password"
                id="password"
                onChange={handleChange}
                value={values.password}
                placeholder="Enter your password"
                className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition duration-200"
              />
              {errors.password && touched.password && (
                <motion.span 
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-red-500 text-sm mt-1 block"
                >
                  {errors.password}
                </motion.span>
              )}
            </div>

            <div className="pt-4">
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Draw your security pattern
              </label>
              <div className="flex justify-center">
                {mounted && (
                  <PatternLock
                    path={path}
                    width={300}
                    height={300}
                    size={3}
                    disabled={disabled}
                    onChange={(newPath) => {
                      if (Array.isArray(newPath)) {
                        setPath(newPath);
                      }
                    }}
                    onFinish={() => {
                      getpath();
                      setDisabled(false);
                    }}
                    connectorThickness={4}
                    connectorRoundedCorners={true}
                    style={{
                      backgroundColor: '#4338ca',
                      borderRadius: '16px',
                      boxShadow: '0 4px 20px rgba(0, 102, 255, 0.1)'
                    }}
                    pointActiveSize={20}
                    pointSize={16}
                    pointBackground="white"
                    pointBorder="1px solid #3b82f6"
                    pointActiveBackground="#3b82f6"
                    pointHoverBackground="#93c5fd"
                    pointHoverBorder="1px solid #93c5fd"
                    pointHoverSize={18}
                    connectorColor="#3b82f6"
                    pathColor="#3b82f6"
                  />
                )}
              </div>
              <AnimatePresence>
                {path.length > 0 && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -10 }}
                    transition={{ duration: 0.2 }}
                    className="flex justify-center mt-2"
                  >
                    <button
                      type="button"
                      onClick={reset}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium transition-colors"
                    >
                      Reset Pattern
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>

          <motion.button
            type="submit"
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white py-3 px-4 rounded-lg font-semibold shadow-md hover:shadow-lg transition-all duration-200"
          >
            Login
          </motion.button>
        </form>
      </div>
    </motion.div>
  );
};

export default Login;